import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import JobRotationAssessmentService from "src/services/jobRotationAssessment/jobRotationAssessmentService";
import { RootState } from "src/store/store";
import { addError } from "src/store/errorBoundarySlice";
import { createToast } from "src/store/toastsSlice";
import { initialTaskToBeCompleted } from "src/jobRotationAssessment/data/taskToBeCompleted";
import {
  INITIAL_MANUAL_INPUT,
  JR_ERRORS_MAPPINGS,
  MANUAL_INPUT_LABELS,
  MAX_VIDEO_UPLOAD_RETRIES,
  taskToBeCompletedStatus,
  uploadingStatus,
  uploadVideoErrorMessages,
} from "src/jobRotationAssessment/constants/jobRotationConstants";
import { TOAST_TIMEOUT } from "src/constants/constants";
import { Segment } from "src/jobRotationAssessment/components/segmentPainter/segmentPainter";
import {
  setIsAssessmentOnEdit,
  setIsDraftAssessmentCompleted,
  setSelectedTab,
} from "src/store/jobRotationAssessmentLibrarySlice";
export interface Task {
  label: string;
  status: string;
}
export interface ManualInputs {
  [key: string]: string | number;
}
interface ManualInputOption {
  id: string;
  text: string;
}
interface AssessmentVideo {
  videoId: string;
  segments: Segment[];
}
export interface JobRotationMetadata {
  label: string;
  name: string;
  options: ManualInputOption[];
}
export interface SaveJobRotationRequest {
  assessmentId: string;
  updatedBy: string;
  videos?: AssessmentVideo[];
  manualInputs?: ManualInputs;
  calculationV2?: boolean;
}
export interface DeleteAssessmentRequest {
  deletedAssessment: { assessmentId: string; status: string };
  deletedBy: string;
}
export interface AssessmentDetailsVideo {
  fileName: string;
  id: string;
  preSignedUrl: string;
  ergoNetDemoVideoUrl: string;
  fallbackPreSignedUrl?: string;
  segments: {
    startTimeInSeconds: number;
    endTimeInSeconds: number;
  }[];
  videoStatus: string;
  errorType?: keyof typeof JR_ERRORS_MAPPINGS;
}
export interface StrainIndexBodyDetails {
  [key: string]: number | undefined;
}
export interface StrainIndex {
  [key: string]: string | number | StrainIndexBodyDetails;
}
export interface ComplementaryJob {
  assessmentId: string;
  assessmentName: string;
  processPath: string;
  strainIndexScore: number;
  improvementPct: number;
  trunkScore: number;
  upperArmScore: number;
}
export interface AssessmentDetails {
  assessmentId: string;
  assessmentName: string;
  processPath: string;
  owner: string;
  status: string;
  videos: AssessmentDetailsVideo[];
  manualInputs: ManualInputs;
  updatedBy: string;
  dateUpdated: string;
  site: string;
  strainIndex?: StrainIndex;
  complementaryJobs?: ComplementaryJob[];
}
export interface CreateAssessmentPayload {
  processPath: string;
  site: string;
  owner: string;
  fileNames: string[];
}
export interface UploadedVideo {
  file: File;
  uploadingStatus: string;
}

export type CreateJobRotationAssessmentState = {
  tasksToBeCompleted: Task[];
  workCycleSubTasks: Task[];
  selectedProcessPath: string;
  isSaveAndContinueEnabled: boolean;
  videosFromDevice: File[];
  isCreateJobRotationLoading: boolean;
  isUploadingDone: boolean;
  uploadedVideos: UploadedVideo[];
  currentCreateAssessmentFlow: string;
  selectedAssessmentId: string;
  isDomActivityBlockerOpen: boolean;
  selectedVideoId: string;
  videoViewMode: string;
  manualInputs: ManualInputs;
  isDraftAssessmentSaved: boolean;
  isAssessmentEditDone: boolean;
  isErrorOnAssessmentDetailsPage: boolean;
  selectedAssessmentDetails?: AssessmentDetails;
  comparisonAssessmentDetails?: AssessmentDetails;
  videoSegmentToEdit?: Segment;
  jobRotationMetadata?: JobRotationMetadata[];
  assessmentStrainIndex?: StrainIndex;
  comparisonStrainIndex?: StrainIndex;
  allVideoFramesForCalculation: boolean;
  selectedAssessmentVideos: { id: string; duration: number }[];
};

const initialState: CreateJobRotationAssessmentState = {
  tasksToBeCompleted: initialTaskToBeCompleted,
  workCycleSubTasks: [],
  selectedProcessPath: "",
  isSaveAndContinueEnabled: false,
  uploadedVideos: [],
  videosFromDevice: [],
  isCreateJobRotationLoading: false,
  isUploadingDone: false,
  currentCreateAssessmentFlow: "",
  selectedAssessmentId: "",
  selectedVideoId: "",
  isDomActivityBlockerOpen: false,
  videoViewMode: "",
  manualInputs: {},
  isDraftAssessmentSaved: false,
  isAssessmentEditDone: false,
  isErrorOnAssessmentDetailsPage: false,
  allVideoFramesForCalculation: false,
  selectedAssessmentVideos: [],
};

export const startVideoUploadProcess = createAsyncThunk(
  "createJobRotationAssessment/startVideoUploadProcess",
  async (payload: CreateAssessmentPayload, { getState, dispatch }) => {
    const state = (await getState()) as RootState;
    const uploadResults: UploadedVideo[] = [];
    try {
      const videoFiles = state.createJobRotationAssessment.uploadedVideos?.map(
        ({ file }) => file,
      );
      const { ergoNetPreSignedUrlList: ergoNetPreSignedUrls } =
        await JobRotationAssessmentService.createJobRotationAssessment(payload);
      if (
        videoFiles &&
        ergoNetPreSignedUrls &&
        videoFiles.length === ergoNetPreSignedUrls.length
      ) {
        for (let i = 0; i < videoFiles.length; i++) {
          dispatch(
            setUploadedVideoStatus({
              file: videoFiles[i],
              uploadingStatus: uploadingStatus.UPLOADING,
            }),
          );
          try {
            await JobRotationAssessmentService.uploadVideoToS3(
              ergoNetPreSignedUrls[i],
              videoFiles[i],
              MAX_VIDEO_UPLOAD_RETRIES,
            );
            dispatch(
              setUploadedVideoStatus({
                file: videoFiles[i],
                uploadingStatus: uploadingStatus.UPLOADED,
              }),
            );

            uploadResults.push({
              file: videoFiles[i],
              uploadingStatus: uploadingStatus.UPLOADED,
            });
          } catch (error: any) {
            dispatch(addError(error?.message));
            dispatch(
              setUploadedVideoStatus({
                file: videoFiles[i],
                uploadingStatus: uploadingStatus.UPLOAD_FAILED,
              }),
            );
            uploadResults.push({
              file: videoFiles[i],
              uploadingStatus: uploadingStatus.UPLOAD_FAILED,
            });
          }
        }
        dispatch(
          createToast({
            type: "success",
            message: [uploadVideoErrorMessages.uploadSuccess],
            timeout: TOAST_TIMEOUT,
          }),
        );
        return uploadResults;
      }
    } catch (error: any) {
      console.log(error);
    }
  },
);
export const getJobRotationAssessmentDetails = createAsyncThunk(
  "createJobRotationAssessment/getJobRotationAssessmentDetails",
  async (assessmentId: string, { getState, dispatch }) => {
    const state = await (getState() as any);
    const isCalculationV2 = state.globalLayout
      .calculationV2FeatureFlag as boolean;
    const response =
      await JobRotationAssessmentService.getJobRotationAssessmentDetails(
        assessmentId,
        isCalculationV2,
      );
    if (!response) {
      dispatch(setIsErrorOnAssessmentDetailsPage(true));
    }
    const assessmentDetails: AssessmentDetails =
      response.data.assessmentDetails;
    if (assessmentDetails) {
      assessmentDetails.videos = assessmentDetails.videos.map((video) => ({
        ...video,
        fallbackPreSignedUrl: video.preSignedUrl,
        preSignedUrl: video.ergoNetDemoVideoUrl || video.preSignedUrl,
      }));
      const subTasks = assessmentDetails.videos.map((video) => {
        return {
          label: video.fileName,
          status: taskToBeCompletedStatus.NOT_STARTED,
        };
      });
      dispatch(setSubTasks(subTasks));
      return assessmentDetails;
    }
  },
);
export const getJobRotationComparisonAssessmentDetails = createAsyncThunk(
  "createJobRotationAssessment/getJobRotationComparisonAssessmentDetails",
  async (assessmentId: string, { dispatch }) => {
    const { data } =
      await JobRotationAssessmentService.getJobRotationAssessmentDetails(
        assessmentId,
      );
    const assessmentDetails: AssessmentDetails = data.assessmentDetails;
    if (assessmentDetails) {
      return assessmentDetails;
    }
  },
);
export const saveJobRotationAssessment = createAsyncThunk(
  "createJobRotationAssessment/saveJobRotationAssessment",
  async (saveJobRotationRequest: SaveJobRotationRequest, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const response =
      await JobRotationAssessmentService.saveJobRotationAssessment(
        saveJobRotationRequest,
      );
    if (response?.status === 200) {
      dispatch(setIsDraftAssessmentSaved(true));
    }
  },
);
export const getJobRotationMetadata = createAsyncThunk(
  "createJobRotationAssessment/getJobRotationMetadata",
  async () => {
    const { data } =
      await JobRotationAssessmentService.getJobRotationMetadata();
    return data.data;
  },
);
export const finalizeJobRotationAssessment = createAsyncThunk(
  "createJobRotationAssessment/finalizeJobRotationAssessment",
  async (finalizePayload: SaveJobRotationRequest, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const state = await (getState() as any);
    const isCalculationV2 = state.globalLayout.calculationV2FeatureFlag;
    if (isCalculationV2) {
      finalizePayload = { ...finalizePayload, calculationV2: true };
    }
    const response =
      await JobRotationAssessmentService.finalizeJobRotationAssessment(
        finalizePayload,
      );
    if (response?.status === 200) {
      dispatch(setSelectedTab("Completed"));
      dispatch(setIsDraftAssessmentCompleted(true));
    }
  },
);

export const editCompletedJobRotationAssessment = createAsyncThunk(
  "createJobRotationAssessment/editCompletedJobRotationAssessment",
  async (editAssessmentRequest: SaveJobRotationRequest, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const state = await (getState() as any);
    const isCalculationV2 = state.globalLayout.calculationV2FeatureFlag;
    if (isCalculationV2) {
      editAssessmentRequest = { ...editAssessmentRequest, calculationV2: true };
    }
    await JobRotationAssessmentService.editCompletedJobRotationAssessment(
      editAssessmentRequest,
    );
    dispatch(setIsAssessmentEditDone(true));
    dispatch(setIsAssessmentOnEdit(false));
  },
);

/** Create Job Rotation Assessment Slice */
const { reducer, actions } = createSlice({
  name: "createJobRotationAssessment",
  initialState,
  reducers: {
    setTaskToBeCompleted: (state, { payload }) => {
      state.tasksToBeCompleted = payload;
    },
    updateTaskStatus: (state, { payload }) => {
      const { label, newStatus } = payload;
      const task = state.tasksToBeCompleted.find(
        (task) => task.label === label,
      );
      if (task) {
        task.status = newStatus;
      }
    },
    setSubTasks: (state, { payload }) => {
      state.workCycleSubTasks = payload;
    },
    updateSubTaskStatus: (state, { payload }) => {
      const { label, newStatus } = payload;
      const task = state.workCycleSubTasks.find((task) => task.label === label);
      if (task) {
        task.status = newStatus;
      }
    },

    setSelectedProcessPath: (state, { payload }) => {
      state.selectedProcessPath = payload;
    },
    setIsSaveAndContinueEnabled: (state, { payload }) => {
      state.isSaveAndContinueEnabled = payload;
    },
    setVideosToBeUploaded: (state, { payload }) => {
      state.videosFromDevice = [...state.videosFromDevice, ...payload];
    },
    removeVideo: (state, { payload }) => {
      state.videosFromDevice = state.videosFromDevice.filter(
        (video) => video.name !== payload.name,
      );
    },
    setIsUploadingDone: (state, { payload }) => {
      state.isUploadingDone = payload;
    },
    setUploadedVideoStatus: (state, { payload }) => {
      const videoToUpdate = state.uploadedVideos.find(
        (video) => video.file.name === payload.file.name,
      );
      if (videoToUpdate) {
        videoToUpdate.uploadingStatus = payload.uploadingStatus;
      }
    },
    clearUploadedVideos: (state) => {
      state.uploadedVideos = [];
    },
    setCurrentCreateAssessmentFlow: (state, { payload }) => {
      state.currentCreateAssessmentFlow = payload;
    },
    setSelectedAssessmentId: (state, { payload }) => {
      state.selectedAssessmentId = payload;
    },
    setSelectedVideoId: (state, { payload }) => {
      state.selectedVideoId = payload;
    },
    setIsDomActivityBlockerOpen: (state, { payload }) => {
      state.isDomActivityBlockerOpen = payload;
    },
    addVideoSegment: (state, { payload }) => {
      const { videoId, newSegment } = payload;
      const activeVideo = state.selectedAssessmentDetails?.videos.find(
        (video) => video.id === videoId,
      );
      if (activeVideo) {
        activeVideo.segments.push(newSegment);
        activeVideo.segments.sort((a, b) => {
          return a.startTimeInSeconds - b.startTimeInSeconds;
        });
      }
    },
    deleteVideoSegment: (state, { payload }) => {
      const { videoId, segmentToDelete } = payload;
      const activeVideo = state.selectedAssessmentDetails?.videos.find(
        (video) => video.id === videoId,
      );
      if (activeVideo) {
        activeVideo.segments = activeVideo.segments.filter(
          (segment) =>
            JSON.stringify(segment) !== JSON.stringify(segmentToDelete),
        );
      }
    },
    editVideoSegment: (state, { payload }) => {
      const { videoId, segmentToEdit, editedSegment } = payload;
      const activeVideo = state.selectedAssessmentDetails?.videos.find(
        (video) => video.id === videoId,
      );
      if (activeVideo) {
        const indexForSegment = activeVideo.segments.findIndex(
          (segment) =>
            JSON.stringify(segment) === JSON.stringify(segmentToEdit),
        );
        if (indexForSegment !== -1) {
          activeVideo.segments[indexForSegment] = editedSegment;
          state.videoSegmentToEdit = editedSegment;
        }
      }
    },
    setvideoViewMode: (state, { payload }) => {
      state.videoViewMode = payload;
    },
    setVideoSegmentToEdit: (state, { payload }) => {
      state.videoSegmentToEdit = payload;
    },
    updateManualInputs: (state, { payload }) => {
      const { label, value } = payload;
      state.manualInputs[label] = value;
    },
    resetManualInput: (state) => {
      state.manualInputs = {};
    },
    setIsDraftAssessmentSaved: (state, { payload }) => {
      state.isDraftAssessmentSaved = payload;
    },
    setIsAssessmentEditDone: (state, { payload }) => {
      state.isAssessmentEditDone = payload;
    },
    setSelectedAssessementDetails: (state, { payload }) => {
      state.selectedAssessmentDetails = payload;
    },
    setIsErrorOnAssessmentDetailsPage: (state, { payload }) => {
      state.isErrorOnAssessmentDetailsPage = payload;
    },
    setAllVideoFramesForCalculation: (state, { payload }) => {
      state.allVideoFramesForCalculation = payload;
    },
    setSelectedAssessmentVideos: (state, { payload }) => {
      if (payload === null) {
        state.selectedAssessmentVideos = [];
      } else {
        state.selectedAssessmentVideos = [
          ...state.selectedAssessmentVideos,
          { id: payload.id, duration: payload.duration },
        ];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startVideoUploadProcess.pending, (state) => {
        state.isCreateJobRotationLoading = true;
        state.uploadedVideos = state.videosFromDevice.map((video) => {
          return { file: video, uploadingStatus: uploadingStatus.PENDING };
        });
        state.videosFromDevice = [];
      })
      .addCase(startVideoUploadProcess.fulfilled, (state, { payload }) => {
        state.isCreateJobRotationLoading = false;
        state.isUploadingDone = true;
      })
      .addCase(startVideoUploadProcess.rejected, (state, { payload }) => {
        state.isCreateJobRotationLoading = false;
        state.isUploadingDone = true;
      });
    builder.addCase(
      getJobRotationAssessmentDetails.fulfilled,
      (state, { payload }) => {
        state.selectedAssessmentDetails = payload;
        if (payload?.manualInputs) {
          if (
            payload.manualInputs["wristNumberOfTimesMovingOutOfNeutral"] === -1
          ) {
            payload.manualInputs["wristNumberOfTimesMovingOutOfNeutral"] = "";
          }
          state.manualInputs = payload.manualInputs;
        }
        if (payload?.strainIndex) {
          state.assessmentStrainIndex = payload.strainIndex;
        }
        const videos = state.selectedAssessmentDetails?.videos;
        videos?.map((video) =>
          video.segments.sort((a, b) => {
            return a.startTimeInSeconds - b.startTimeInSeconds;
          }),
        );
      },
    );
    builder.addCase(
      getJobRotationComparisonAssessmentDetails.fulfilled,
      (state, { payload }) => {
        state.comparisonAssessmentDetails = payload;
        if (payload?.strainIndex) {
          state.comparisonStrainIndex = payload.strainIndex;
        }
      },
    );
    builder.addCase(getJobRotationMetadata.fulfilled, (state, { payload }) => {
      if (payload) {
        const metadata: JobRotationMetadata[] = payload.map(
          (data: JobRotationMetadata, index: number) => {
            return {
              label: MANUAL_INPUT_LABELS[index],
              name: data.name,
              options: data.options,
            };
          },
        );
        state.jobRotationMetadata = metadata;
      }
    });
  },
});

export const {
  setTaskToBeCompleted,
  updateTaskStatus,
  setSubTasks,
  updateSubTaskStatus,
  setSelectedProcessPath,
  setIsSaveAndContinueEnabled,
  setVideosToBeUploaded,
  removeVideo,
  setIsUploadingDone,
  setUploadedVideoStatus,
  clearUploadedVideos,
  setCurrentCreateAssessmentFlow,
  setSelectedAssessmentId,
  setIsDomActivityBlockerOpen,
  addVideoSegment,
  editVideoSegment,
  setSelectedVideoId,
  deleteVideoSegment,
  setvideoViewMode,
  setVideoSegmentToEdit,
  updateManualInputs,
  resetManualInput,
  setIsDraftAssessmentSaved,
  setIsAssessmentEditDone,
  setSelectedAssessementDetails,
  setIsErrorOnAssessmentDetailsPage,
  setAllVideoFramesForCalculation,
  setSelectedAssessmentVideos,
} = actions;

export default reducer;
